import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Progress, Spinner } from 'reactstrap';
import dayjs from 'dayjs';

import { dateInputFieldFormat, formatDateString } from '~libs/dayjs';

import ErrorResponse from '~components/ErrorResponse';
import Icon, { faArrowUpRightFromSquare } from '~components/icon';

import { getEstimate } from '~appointments/actions/utilities/bulkUpdatePackages';

import { selectClientPackages } from '~main/selectors/clients/clientPackages';
import { selectLookupClients } from '~main/selectors/clients/clients';

interface ComponentProps {
  value: any;
}

type Row = {
  label: string;
  description?: string;
  value: string | number;
};

const Validate: React.FC<ComponentProps> = ({ value }) => {
  const dispatch = useAppDispatch();
  const { is_repeat, repeat_end, date, old_package_id, new_package_id, client_id } = value;
  const [isValidating, setIsValidating] = useState(true);
  const [count, setCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const packages = useAppSelector(selectClientPackages);
  const clients = useAppSelector(selectLookupClients);

  useEffect(() => {
    getEstimates();
  }, []);

  const getEstimates = async () => {
    setIsValidating(true);

    const params = {
      ...(is_repeat === true ? { is_repeat, repeat_end } : { date }),
      old_package_id,
      client_id,
    };

    const { payload, error } = await dispatch(getEstimate(params));
    const { success, data } = payload ?? {};
    if (success) {
      setCount(data.count);
    } else {
      setErrorMessage(error?.message ?? 'Something unexpected happened, please contact your administrator');
    }

    setIsValidating(false);
  };

  const renderRow = ({ label, description, value }: Row) => {
    return (
      <div className="my-2">
        <div className="row">
          <div className="col">{label}</div>
          <div className="col col-sm-8">
            {isValidating ? <Spinner type="border" size="sm" color="info" /> : <span>{value}</span>}
          </div>
        </div>
        {description && <div className="small text-info">{description}</div>}
      </div>
    );
  };

  let range = date;
  if (is_repeat) range = repeat_end;

  const from = formatDateString(dayjs(range.gte, dateInputFieldFormat));
  let to = '...';
  if (range.lte) to = formatDateString(dayjs(range.lte, dateInputFieldFormat));
  const oldPackageName = packages.find(({ id }) => id === old_package_id)?.name;
  const newPackageName = packages.find(({ id }) => id === new_package_id)?.name;

  const hrefParams = JSON.stringify({
    ...(is_repeat ? { repeat_end } : { date }),
    package_fixed_id: String(old_package_id),
    ...(client_id?.length > 0 && { client_id }),
  });
  const href = `/appointments${is_repeat ? '/repeats' : ''}?search=${hrefParams}`;

  return (
    <div>
      <ErrorResponse message={errorMessage} />
      {isValidating && (
        <Progress className="text-center w-100" color="success" style={{ height: '25px' }} value={100} animated>
          Validating
        </Progress>
      )}
      <div className="mx-4">
        {renderRow({
          label: `${is_repeat === true ? 'Repeat ' : ''}Date Range`,
          value: `${from} to ${to} (incl.)`,
        })}
        {renderRow({
          label: `${is_repeat === true ? 'Repeat ' : ''}Clients`,
          value: `${client_id?.length > 0 ? client_id.map((id: number) => clients.find((client) => client.id === id)?.name).join(', ') : 'All'}`,
        })}
        {renderRow({
          label: 'Package FROM',
          description: 'Package currently attached to appointment',
          value: oldPackageName,
        })}
        {renderRow({
          label: 'Package TO',
          description: 'Appointment will be updated to this package',
          value: newPackageName,
        })}
        {renderRow({
          label: 'Affected appointments',
          description: 'Appointment matches date range and chosen OLD package',
          value: count,
        })}
      </div>
      <div className="d-flex justify-content-end mt-2">
        <a href={href} target="_blank" rel="noreferrer" className="link">
          Review affected appointments
          <Icon icon={faArrowUpRightFromSquare} className="hover ms-2" size="1x" />
        </a>
      </div>
    </div>
  );
};

export default Validate;
